import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ApperIcon from '@/components/ApperIcon';
import { toast } from 'sonner';
import { PRODUCT_FIELDS, unwrap } from './consoleConfig';

export const route = { path: '/admin/products/:id', layout: 'owner', access: "profile === 'Admin_c'" };

export default function ProductDetail() {
  const { id } = useParams();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const { data, loading, error, run } = useFetch(async () => {
    const response = await sdk.table('products_c').select(PRODUCT_FIELDS).get(id);
    if (!response?.success) throw new Error(response?.message || 'Could not load this product.');
    return response.data;
  }, [id]);

  function startEditing() {
    setDraft(Object.fromEntries(PRODUCT_FIELDS.map((field) => [field, data?.[field] ?? ''])));
    setEditing(true);
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await sdk.table('products_c').update({ Id: data.Id, ...draft });
      if (!response?.data?.length) throw new Error(response?.messages?.[0] || response?.message || 'Product was not saved.');
      toast.success('Product details saved.');
      setEditing(false);
      await run();
    } catch (saveError) {
      toast.error(saveError.message || 'Could not save the product.');
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (busy || data?.publication_status_c === 'Archived') return;
    setBusy(true);
    try {
      const response = await sdk.table('products_c').update({ Id: data.Id, publication_status_c: 'Archived' });
      if (!response?.data?.length) throw new Error(response?.messages?.[0] || response?.message || 'Product was not archived.');
      toast.success('Product archived. Its history is preserved.');
      await run();
    } catch (archiveError) {
      toast.error(archiveError.message || 'Could not archive the product.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && data === undefined) return <div className="space-y-4"><div className="h-8 w-40 animate-pulse rounded bg-muted" /><div className="h-56 animate-pulse rounded-2xl bg-muted" /></div>;
  if (error) return <div role="alert" className="rounded-xl border border-destructive/30 bg-card p-5"><p className="font-semibold">Could not load the product.</p><p className="mt-2 text-sm text-muted-foreground">{error}</p><Button variant="outline" className="mt-4 min-h-11" onClick={() => run()}>Try again</Button></div>;
  if (!data) return <div className="rounded-xl border border-border bg-card p-6"><p className="font-heading text-2xl font-semibold">Product not found</p><Link to="/admin" className="mt-4 inline-flex min-h-11 items-center gap-2 text-primary"><ApperIcon name="ArrowLeft" size={17} />Back to products</Link></div>;

  return (
    <article className="mx-auto max-w-4xl space-y-6">
      <Link to="/admin" className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-primary hover:underline"><ApperIcon name="ArrowLeft" size={17} />Back to products</Link>
      <header className="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Catalog record</p><h1 className="mt-2 font-heading text-4xl font-semibold">{data.Name}</h1><p className="mt-2 text-sm text-muted-foreground">Current status: {data.publication_status_c || 'Draft'}</p></div>
        {!editing && <div className="flex flex-wrap gap-2"><Button variant="outline" className="min-h-11" onClick={startEditing}><ApperIcon name="Pencil" size={16} />Edit product</Button>{data.publication_status_c !== 'Archived' && <Button variant="outline" className="min-h-11" onClick={archive} disabled={busy}>{busy ? 'Archiving…' : 'Archive product'}</Button>}</div>}
      </header>
      {editing ? (
        <form onSubmit={save} className="grid gap-4 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2 sm:p-6">
          <label className="space-y-2 text-sm font-medium sm:col-span-2">Product name<Input className="min-h-11" required value={draft.Name} onChange={(event) => setDraft((current) => ({ ...current, Name: event.target.value }))} /></label>
          <label className="space-y-2 text-sm font-medium">Category<select className="min-h-11 w-full rounded-lg border border-input bg-background px-3" value={draft.category_c} onChange={(event) => setDraft((current) => ({ ...current, category_c: event.target.value }))}><option>Tea</option><option>Coffee</option></select></label>
          <label className="space-y-2 text-sm font-medium">Publication status<select className="min-h-11 w-full rounded-lg border border-input bg-background px-3" value={draft.publication_status_c} onChange={(event) => setDraft((current) => ({ ...current, publication_status_c: event.target.value }))}><option>Draft</option><option>Published</option><option>Archived</option></select></label>
          {PRODUCT_FIELDS.filter((field) => ['description_c', 'tasting_notes_c', 'brew_guide_c'].includes(field)).map((field) => <label key={field} className="space-y-2 text-sm font-medium sm:col-span-2">{field.replace(/_c$/, '').replaceAll('_', ' ')}<textarea className="min-h-24 w-full rounded-lg border border-input bg-background p-3" value={draft[field]} onChange={(event) => setDraft((current) => ({ ...current, [field]: event.target.value }))} /></label>)}
          <label className="space-y-2 text-sm font-medium sm:col-span-2">Product image URL<Input className="min-h-11" type="url" value={draft.image_url_c} onChange={(event) => setDraft((current) => ({ ...current, image_url_c: event.target.value }))} /></label>
          <div className="flex flex-col-reverse gap-2 sm:col-span-2 sm:flex-row sm:justify-end"><Button type="button" variant="outline" className="min-h-11" onClick={() => setEditing(false)}>Cancel</Button><Button type="submit" className="min-h-11" disabled={busy}>{busy ? 'Saving…' : 'Save product'}</Button></div>
        </form>
      ) : (
        <dl className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
          {PRODUCT_FIELDS.map((field) => <div key={field} className="min-w-0 bg-card p-4 sm:p-5"><dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{field === 'Name' ? 'Product name' : field.replace(/_c$/, '').replaceAll('_', ' ')}</dt><dd className="mt-2 whitespace-pre-wrap break-words text-sm">{data[field] || '—'}</dd></div>)}
        </dl>
      )}
    </article>
  );
}